
import NavBar from '../../components/feature/NavBar';
import TabBar from '../../components/feature/TabBar';
import Card from '../../components/base/Card';

export default function Progress() {
  const stats = [
    { label: 'Games Played', value: '24', icon: 'ri-gamepad-line', color: 'bg-blue-500' },
    { label: 'Words Learned', value: '156', icon: 'ri-book-open-line', color: 'bg-green-500' },
    { label: 'Reading Level', value: 'B2', icon: 'ri-trophy-line', color: 'bg-yellow-500' },
    { label: 'Study Streak', value: '7 days', icon: 'ri-fire-line', color: 'bg-red-500' }
  ];
  
  const recentGames = [
    { game: 'Snakes & Ladders', date: '2024-01-15', score: 85, words: 12 },
    { game: 'Word Puzzle', date: '2024-01-14', score: 92, words: 8 },
    { game: 'Spelling Race', date: '2024-01-13', score: 78, words: 15 },
    { game: 'Snakes & Ladders', date: '2024-01-12', score: 88, words: 10 },
    { game: 'Word Puzzle', date: '2024-01-11', score: 95, words: 6 }
  ];
  
  const achievements = [
    { title: 'First Win!', description: 'Won your first game', icon: 'ri-trophy-line', earned: true },
    { title: 'Word Master', description: 'Read 100 words correctly', icon: 'ri-book-line', earned: true },
    { title: 'Speed Reader', description: 'Complete a game in under 5 minutes', icon: 'ri-flashlight-line', earned: true },
    { title: 'Team Player', description: 'Play 10 multiplayer games', icon: 'ri-group-line', earned: false },
    { title: 'Perfect Score', description: 'Get 100% accuracy in a game', icon: 'ri-star-line', earned: false },
    { title: 'Streak Master', description: 'Play for 30 consecutive days', icon: 'ri-fire-line', earned: false }
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
      <NavBar />
      <TabBar />
      
      <div className="pt-20 md:pt-24 pb-20 md:pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Your Progress</h2>
            <p className="text-lg md:text-xl text-gray-600">Track your reading journey and achievements</p>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12">
            {stats.map((stat, index) => (
              <Card key={index} className="text-center">
                <div className={`w-12 h-12 md:w-16 md:h-16 ${stat.color} rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4`}>
                  <i className={`${stat.icon} text-white text-xl md:text-2xl`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">{stat.value}</div>
                <div className="text-sm md:text-base text-gray-600">{stat.label}</div>
              </Card>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Games */}
            <Card>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2d5a35] to-green-600 rounded-xl flex items-center justify-center">
                  <i className="ri-history-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Recent Games</h3>
                  <p className="text-gray-600 text-sm">Your latest game sessions</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {recentGames.map((game, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <div className="font-medium text-gray-800">{game.game}</div>
                      <div className="text-sm text-gray-500">{game.date}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-[#2d5a35]">{game.score}%</div>
                      <div className="text-sm text-gray-600">{game.words} words</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            
            {/* Achievements */}
            <Card>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center">
                  <i className="ri-medal-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Achievements</h3>
                  <p className="text-gray-600 text-sm">Unlock rewards as you learn</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {achievements.map((achievement, index) => (
                  <div 
                    key={index} 
                    className={`p-4 rounded-xl border-2 transition-all ${
                      achievement.earned 
                        ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        achievement.earned 
                          ? 'bg-yellow-500 text-white' 
                          : 'bg-gray-300 text-gray-500'
                      }`}>
                        <i className={`${achievement.icon} text-lg`}></i>
                      </div>
                      <div className="flex-1">
                        <div className={`font-medium ${achievement.earned ? 'text-gray-800' : 'text-gray-500'}`}>
                          {achievement.title}
                        </div>
                        <div className={`text-sm ${achievement.earned ? 'text-gray-600' : 'text-gray-400'}`}>
                          {achievement.description}
                        </div>
                      </div>
                      {achievement.earned && (
                        <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          
          {/* Weekly Progress Chart Placeholder */}
          <Card className="mt-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <i className="ri-line-chart-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Weekly Progress</h3>
                <p className="text-gray-600 text-sm">Your reading improvement over time</p>
              </div>
            </div>
            
            <div className="h-64 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl flex items-center justify-center">
              <img 
                src="https://readdy.ai/api/search-image?query=clean%20simple%20line%20chart%20showing%20upward%20progress%20trend%2C%20minimalist%20design%20with%20green%20and%20blue%20colors%2C%20educational%20progress%20visualization%2C%20soft%20gradients%2C%20modern%20dashboard%20style%2C%20isolated%20on%20light%20background%2C%20professional%20data%20visualization%2C%20reading%20progress%20graph&width=600&height=300&seq=chart1&orientation=landscape"
                alt="Progress Chart"
                className="max-w-full max-h-full object-contain rounded-xl"
              />
            </div>
            
            <div className="mt-4 p-4 bg-green-50 rounded-xl">
              <p className="text-[#2d5a35] font-medium text-center">
                🎉 Great job! You've improved your reading accuracy by 15% this week!
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
